import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { fetchAreas } from "../redux/slices/areaSlice";
import axios from "axios";
import { server } from "../server";
import { toast } from "react-toastify";
import useAuth from "../useAuth";
import {
  Pagination,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import { Close } from "@mui/icons-material";
import { FaTrash } from "react-icons/fa";

const headersMap = {
  schemeName: "Scheme Name",
  proposalName: "Proposal Name",
  financialYear: "Financial Year",
  sector: "Sector",
  subSector: "Sub Sector",
  district: "District",
  block: "Block",
  gramPanchayat: "Gram Panchayat",
  landAddress: "Land Address",
  latitude: "Latitude",
  longitude: "Longitude",
  utilityPurpose: "Utility Purpose",
  impactDescription: "Impact Description",
  landNature: "Land Nature",
  landSuitability: "Land Suitability",
  ddcCumCEOName: "DDC cum CEO Name",
  districtEngineerName: "District Engineer Name",
  ddo: "DDO",
  ddoCode: "DDO Code",
  totalAllocated: "Total Allocated Amount",
  allocatedAmount: "Allocated Amount",
  allocationLetterNo: "Allocation Letter No",
  technicalSanctionAmount: "Technical Sanction Amount",
  administrativeApprovalAmount: "Administrative Approval Amount",
  amountRemaining: "Amount Remaining",
  totalSpent: "Amount Spent",
  ucAmount: "UC Amount",
  needAssessment: "Need Assessment",
  feasibilityStudy: "Feasibility Study",
  dpr: "DPR Status",
  revenueGeneration: "Revenue Generation",
  boardApproval: "Board Approval",
  technicalSanctionAuthority: "Technical Sanction Authority",
  administrativeApprovalAuthority: "Administrative Approval Authority",
  administrativeApprovalLetterNo: "Admin Approval Letter No",
  administrativeApprovalDate: "Admin Approval Date",
  schemeStartDate: "Scheme Start Date",
  completionDate: "Completion Date",
  workOrderDate: "Work Order Date",
  tenderStatus: "Tender Status",
  agencyName: "Agency Name",
  ucSentLetterNo: "UC Sent Letter No",
  ucSentDate: "UC Sent Date",
  lastFieldVisitDate: "Last Field Visit Date",
  fieldVisitOfficials: "Field Visit Officials",
  remarks: "Remarks",
  coReport: "CO Report",
  formStatus: "Form Status",
  schemeStatus: "Scheme Status",
  paymentStatus: "Payment Status",
  progressPercentage: "Progress Percentage"
};

const escapeCSVValue = (val) => {
  if (val === null || val === undefined) return "";
  let stringVal = String(val);
  stringVal = stringVal.replace(/"/g, '""');
  if (stringVal.includes(",") || stringVal.includes('"') || stringVal.includes("\n") || stringVal.includes("\r")) {
    return `"${stringVal}"`;
  }
  return stringVal;
};

const getFieldValue = (scheme, key) => {
  if (key === "latitude") {
    return scheme.coordinates?.latitude || "";
  }
  if (key === "longitude") {
    return scheme.coordinates?.longitude || "";
  }
  if (key === "revenueGeneration" || key === "boardApproval") {
    return scheme[key] ? "Yes" : "No";
  }
  if (
    key === "administrativeApprovalDate" ||
    key === "schemeStartDate" ||
    key === "completionDate" ||
    key === "workOrderDate" ||
    key === "ucSentDate" ||
    key === "lastFieldVisitDate"
  ) {
    if (!scheme[key]) return "";
    return new Date(scheme[key]).toLocaleDateString("en-IN");
  }
  return scheme[key] !== undefined && scheme[key] !== null ? scheme[key] : "";
};

const getFinancialYearOptions = () => {
  const years = [];
  const currentDate = new Date();
  const currentYear = currentDate.getFullYear();
  const startYear = currentDate.getMonth() >= 3 ? currentYear : currentYear - 1;
  const endYear = 2015;
  for (let y = startYear; y >= endYear; y--) {
    years.push(`${y}-${y + 1}`);
  }
  return years;
};

const LegacySchemesList = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const dispatch = useDispatch();
  const { areas } = useSelector((state) => state.area);
  const [schemes, setSchemes] = useState([]);
  const [schemeStats, setSchemeStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [filters, setFilters] = useState({
    searchParams: "",
    district: "",
    block: "",
    gramPanchayat: "",
    sector: "",
    subSector: "",
    financialYear: "",
    schemeStatus: "",
    tenderStatus: "",
    paymentStatus: "",
    ddcCumCEOName: "",
    districtEngineerName: "",
    ddo: "",
  });

  useEffect(() => {
    dispatch(fetchAreas());
  }, [dispatch]);

  const uniqueDistricts = React.useMemo(() => {
    if (user?.userType !== "admin" && user?.districtName) {
      return [user.districtName];
    }
    const districts = areas?.map((a) => a.district) || [];
    return [...new Set(districts)].sort();
  }, [areas, user]);

  const effectiveDistrict = React.useMemo(() => {
    return (user?.userType !== "admin" ? user?.districtName : filters?.district)?.toUpperCase();
  }, [user, filters?.district]);

  const uniqueBlocks = React.useMemo(() => {
    if (!effectiveDistrict) return [];
    const blocks = (areas || [])
      .filter((a) => a.district === effectiveDistrict)
      .map((a) => a.block);
    return [...new Set(blocks)].sort();
  }, [areas, effectiveDistrict]);

  const financialYearOptions = getFinancialYearOptions();

  const schemeStatusOptions = [
    "Ongoing",
    "Completed",
    "On Hold",
    "Cancelled",
    "Partially Completed",
  ];

  const sectorOptions = [
    "Infrastructure - GP",
    "Asset Creation",
    "Repair/Renovation",
    "Water Supply",
    "Sanitation",
    "Road Construction",
    "Building Construction",
    "Electrical Works",
    "Other",
  ];

  const subSectorOptions = [
    "GP Bhawan - New",
    "GP Bhawan - Repair",
    "Office Renovation",
    "Dak Bungalow",
    "Bus Stand",
    "Market Complex",
    "Marriage Hall",
    "Community Hall",
    "Water Tank",
    "Drainage System",
    "Road Construction",
    "Street Lights",
    "Park Development",
    "Other",
  ];

  const [showFilters, setShowFilters] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const filtersRef = useRef(null);

  // Pagination states
  const [pagination, setPagination] = useState({
    pageNo: 1,
    pageSize: 10,
    totalCount: 0,
    totalPages: 1,
  });

  const [filterOptions, setFilterOptions] = useState({
    districts: [],
    blocks: [],
    gramPanchayats: [],
    sectors: [],
    subSectors: [],
    financialYears: [],
    schemeStatuses: [],
    tenderStatuses: [],
    paymentStatuses: [],
    ddcCumCEOs: [],
    districtEngineers: [],
    ddos: [],
  });

  const filterCategoryColors = {
    location: {
      bg: "bg-blue-100",
      border: "border-blue-300",
      text: "text-blue-800",
    },
    sector: {
      bg: "bg-purple-100",
      border: "border-purple-300",
      text: "text-purple-800",
    },
    status: {
      bg: "bg-green-100",
      border: "border-green-300",
      text: "text-green-800",
    },
    financial: {
      bg: "bg-orange-100",
      border: "border-orange-300",
      text: "text-orange-800",
    },
    officer: {
      bg: "bg-indigo-100",
      border: "border-indigo-300",
      text: "text-indigo-800",
    },
    search: {
      bg: "bg-gray-100",
      border: "border-gray-300",
      text: "text-gray-800",
    },
  };

  useEffect(() => {
    fetchSchemes();
  }, [filters, pagination.pageNo, pagination.pageSize]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (filtersRef.current && !filtersRef.current.contains(event.target)) {
        setShowFilters(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const fetchSchemes = async () => {
    try {
      setLoading(true);

      // Prepare query parameters
      const params = {
        pageNo: pagination.pageNo,
        pageSize: pagination.pageSize,
        userWise: "true",
        searchParams: filters.searchParams,
        schemeType: "legacy", // Filter specifically for legacy schemes
      };

      // Add other filters only if they have values
      Object.entries(filters).forEach(([key, value]) => {
        if (value && key !== "searchParams") {
          params[key] = value;
        }
      });

      const { data } = await axios.get(`${server}/scheme/all`, {
        params,
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });

      setSchemes(data?.data || []);
      setPagination((prev) => ({
        ...prev,
        totalCount: data?.count || 0,
        totalPages: Math.ceil((data?.count || 0) / pagination.pageSize),
      }));

      setSchemeStats({
        total: data?.count,
        totalDraftSchemes: data?.totalDraftSchemes,
        totalCompletedSchemes: data?.totalCompletedSchemes,
        totalAllocationAmount: data?.totalAllocationAmount,
      });

      updateFilterOptions(data?.data || []);
    } catch (error) {
      console.error("Error fetching legacy schemes:", error);
      toast.error("Failed to fetch legacy schemes");
    } finally {
      setLoading(false);
    }
  };

  const updateFilterOptions = (schemesData) => {
    const options = {
      districts: [
        ...new Set(schemesData.map((s) => s.district).filter(Boolean)),
      ],
      blocks: [...new Set(schemesData.map((s) => s.block).filter(Boolean))],
      gramPanchayats: [
        ...new Set(schemesData.map((s) => s.gramPanchayat).filter(Boolean)),
      ],
      sectors: [...new Set(schemesData.map((s) => s.sector).filter(Boolean))],
      subSectors: [
        ...new Set(schemesData.map((s) => s.subSector).filter(Boolean)),
      ],
      financialYears: [
        ...new Set(schemesData.map((s) => s.financialYear).filter(Boolean)),
      ],
      schemeStatuses: [
        ...new Set(schemesData.map((s) => s.schemeStatus).filter(Boolean)),
      ],
      tenderStatuses: [
        ...new Set(schemesData.map((s) => s.tenderStatus).filter(Boolean)),
      ],
      paymentStatuses: [
        ...new Set(schemesData.map((s) => s.paymentStatus).filter(Boolean)),
      ],
      ddcCumCEOs: [
        ...new Set(schemesData.map((s) => s.ddcCumCEOName).filter(Boolean)),
      ],
      districtEngineers: [
        ...new Set(
          schemesData.map((s) => s.districtEngineerName).filter(Boolean)
        ),
      ],
      ddos: [...new Set(schemesData.map((s) => s.ddo).filter(Boolean))],
    };

    setFilterOptions(options);
  };

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    if (key !== "searchParams") {
      setPagination((prev) => ({ ...prev, pageNo: 1 }));
    }
  };

  const handleSearchChange = (value) => {
    setFilters((prev) => ({ ...prev, searchParams: value }));
    setPagination((prev) => ({ ...prev, pageNo: 1 }));
  };

  const removeFilter = (key) => {
    setFilters((prev) => ({ ...prev, [key]: "" }));
    if (key !== "searchParams") {
      setPagination((prev) => ({ ...prev, pageNo: 1 }));
    }
  };

  const handleSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
  };

  const clearFilters = () => {
    setFilters({
      searchParams: "",
      district: "",
      block: "",
      gramPanchayat: "",
      sector: "",
      subSector: "",
      financialYear: "",
      schemeStatus: "",
      tenderStatus: "",
      paymentStatus: "",
      ddcCumCEOName: "",
      districtEngineerName: "",
      ddo: "",
    });
    setPagination((prev) => ({ ...prev, pageNo: 1 }));
  };

  const handlePageChange = (event, value) => {
    setPagination((prev) => ({ ...prev, pageNo: value }));
  };

  const handlePageSizeChange = (event) => {
    setPagination((prev) => ({
      ...prev,
      pageSize: event.target.value,
      pageNo: 1,
    }));
  };

  const formatAmount = (amount) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 0,
    }).format(amount || 0);
  };

  const getActiveFilterCount = () => {
    return Object.values(filters).filter(
      (value) => value !== "" && value !== null && value !== undefined
    ).length;
  };

  const getFilterCategory = (key) => {
    const categoryMap = {
      district: "location",
      block: "location",
      gramPanchayat: "location",
      sector: "sector",
      subSector: "sector",
      financialYear: "financial",
      schemeStatus: "status",
      tenderStatus: "status",
      paymentStatus: "status",
      ddcCumCEOName: "officer",
      districtEngineerName: "officer",
      ddo: "officer",
      searchParams: "search",
    };
    return categoryMap[key] || "search";
  };

  const getFilterDisplayName = (key) => {
    const displayNames = {
      searchParams: "Search",
      district: "District",
      block: "Block",
      gramPanchayat: "Gram Panchayat",
      sector: "Sector",
      subSector: "Sub Sector",
      financialYear: "Financial Year",
      schemeStatus: "Scheme Status",
      tenderStatus: "Tender Status",
      paymentStatus: "Payment Status",
      ddcCumCEOName: "DDC-cum-CEO",
      districtEngineerName: "District Engineer",
      ddo: "DDO",
    };
    return displayNames[key] || key;
  };

  const getAppliedFilters = () => {
    return Object.entries(filters)
      .filter(([key, value]) => value && value !== "")
      .map(([key, value]) => ({
        key,
        value,
        category: getFilterCategory(key),
        displayName: getFilterDisplayName(key),
      }));
  };

  const handleRowClick = (schemeId) => {
    navigate(`/scheme/${schemeId}`);
  };

  const handleDelete = async (id) => {
    let userRes = window.confirm("Are you sure, you want to delete this legacy proposal?");
    if (!userRes) return;

    try {
      setLoading(true);
      const { data } = await axios.delete(`${server}/scheme/deleteById`, {
        params: {
          schemeId: id,
        },
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });

      if (data.success) {
        toast.success("Legacy proposal deleted successfully");
        fetchSchemes();
      }
    } catch (error) {
      console.error("Error deleting legacy scheme:", error);
      toast.error(error?.response?.data?.message || "Failed to delete legacy proposal");
    } finally {
      setLoading(false);
    }
  };

  const handleExportToExcel = async () => {
    try {
      setExporting(true);
      toast.info("Preparing Excel export, please wait...", { autoClose: 2000 });

      const params = {
        pageNo: 1,
        pageSize: 100000,
        userWise: "true",
        searchParams: filters.searchParams,
        schemeType: "legacy",
      };

      Object.entries(filters).forEach(([key, value]) => {
        if (value && key !== "searchParams") {
          params[key] = value;
        }
      });

      const { data } = await axios.get(`${server}/scheme/all`, {
        params,
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });

      const allSchemes = data?.data || [];
      if (allSchemes.length === 0) {
        toast.info("No legacy data available to export");
        return;
      }

      const headers = Object.keys(headersMap);
      const csvRows = [];

      csvRows.push(headers.map(h => headersMap[h]).map(escapeCSVValue).join(","));

      allSchemes.forEach(scheme => {
        const values = headers.map(header => {
          const val = getFieldValue(scheme, header);
          return escapeCSVValue(val);
        });
        csvRows.push(values.join(","));
      });

      const csvContent = "\uFEFF" + csvRows.join("\n");
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.setAttribute("href", url);
      link.setAttribute("download", `Legacy_Proposal_List_${new Date().toISOString().slice(0, 10)}.csv`);
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast.success("Excel legacy data list exported successfully");
    } catch (error) {
      console.error("Error exporting legacy schemes:", error);
      toast.error("Failed to export legacy schemes to Excel");
    } finally {
      setExporting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-2 text-sm text-gray-600">Loading legacy schemes...</p>
        </div>
      </div>
    );
  }

  const appliedFilters = getAppliedFilters();

  return (
    <div className="">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Legacy Proposals List</h1>
            <p className="text-gray-600 mt-2">
              Manage and monitor legacy development schemes
            </p>
          </div>
          <button
            onClick={handleExportToExcel}
            disabled={exporting}
            className="inline-flex items-center px-4 py-2.5 bg-emerald-600 hover:bg-emerald-700 disabled:bg-emerald-400 text-white rounded-lg font-semibold shadow transition-all text-sm gap-2 active:scale-95 self-start sm:self-auto cursor-pointer"
          >
            {exporting ? (
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
            ) : (
              <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"></path>
              </svg>
            )}
            {exporting ? "Exporting..." : "Export Excel (All Fields)"}
          </button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <div className="bg-white rounded-lg p-5 shadow border border-blue-200">
            <div className="flex items-center">
              <div className="p-2 bg-blue-100 rounded-lg">
                <span className="text-blue-600 text-lg">📊</span>
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-600">Total Legacy Schemes</p>
                <p className="text-xl font-bold text-gray-900">{schemeStats.total}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg p-5 shadow border border-green-200">
            <div className="flex items-center">
              <div className="p-2 bg-green-100 rounded-lg">
                <span className="text-green-600 text-lg">✅</span>
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-600">Completed</p>
                <p className="text-xl font-bold text-green-600">{schemeStats?.totalCompletedSchemes}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg p-5 shadow border border-yellow-200">
            <div className="flex items-center">
              <div className="p-2 bg-yellow-100 rounded-lg">
                <span className="text-yellow-600 text-lg">⚡</span>
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-600">Draft</p>
                <p className="text-xl font-bold text-yellow-600">{schemeStats?.totalDraftSchemes}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg p-5 shadow border border-purple-200">
            <div className="flex items-center">
              <div className="p-2 bg-purple-100 rounded-lg">
                <span className="text-purple-600 text-lg">💰</span>
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-600">Total Allocation</p>
                <p className="text-xl font-bold text-gray-900">₹{schemeStats?.totalAllocationAmount?.toLocaleString("en-In")}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Search and Filter Bar */}
        <div
          ref={filtersRef}
          className="bg-white/90 backdrop-blur-md rounded-2xl shadow-sm border border-slate-200 p-6 mb-6 relative z-10"
        >
          <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-center">
            <div className="flex-1 w-full">
              <div className="relative group">
                <input
                  type="text"
                  placeholder="Search legacy schemes by name, location, or sector..."
                  value={filters.searchParams}
                  onChange={(e) => handleSearchChange(e.target.value)}
                  className="w-full pl-12 pr-4 py-3 border border-slate-200 rounded-xl focus:ring-4 focus:ring-blue-500/20 focus:border-blue-500 bg-slate-50 hover:bg-white transition-all duration-300 text-slate-700 shadow-sm"
                />
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none group-focus-within:text-blue-500 transition-colors">
                  <svg className="w-5 h-5 text-slate-400 group-focus-within:text-blue-500 transition-colors" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>
                </div>
              </div>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`flex items-center px-5 py-3 rounded-xl font-semibold shadow-sm transition-all duration-300 active:scale-95 border ${showFilters ? 'bg-blue-50 text-blue-700 border-blue-200' : 'bg-white text-slate-700 border-slate-200 hover:bg-slate-50'}`}
              >
                <svg className="w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" /></svg>
                Filters
                {getActiveFilterCount() > 0 && (
                  <span className="ml-2 bg-blue-600 text-white rounded-full px-2 py-0.5 text-xs flex items-center justify-center font-bold">
                    {getActiveFilterCount()}
                  </span>
                )}
              </button>

              {getActiveFilterCount() > 0 && (
                <button
                  onClick={clearFilters}
                  className="flex items-center px-5 py-3 bg-rose-50 text-rose-600 border border-rose-200 rounded-xl hover:bg-rose-100 font-semibold shadow-sm transition-all duration-300 active:scale-95"
                >
                  <svg className="w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                  Clear All
                </button>
              )}
            </div>
          </div>

          {/* Applied Filters */}
          {appliedFilters.length > 0 && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              className="mt-4 flex flex-wrap gap-2"
            >
              {appliedFilters.map((filter) => (
                <motion.div
                  key={filter.key}
                  initial={{ scale: 0.8, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  className={`inline-flex items-center px-3 py-1 rounded-lg border text-sm font-medium ${filterCategoryColors[filter.category].bg
                    } ${filterCategoryColors[filter.category].border} ${filterCategoryColors[filter.category].text
                    }`}
                >
                  <span className="mr-1">{filter.displayName}:</span>
                  <span className="font-semibold">{filter.value}</span>
                  <Close
                    onClick={() => removeFilter(filter.key)}
                    className="text-rose-600 ml-1 cursor-pointer"
                    fontSize="small"
                  />
                </motion.div>
              ))}
            </motion.div>
          )}

          {/* Advanced Filters */}
          <AnimatePresence>
            {showFilters && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                className="mt-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4"
              >
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    District
                  </label>
                  <select
                    value={filters.district}
                    onChange={(e) =>
                      handleFilterChange("district", e.target.value)
                    }
                    disabled={user?.userType !== "admin"}
                    className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                  >
                    <option value="">All Districts</option>
                    {uniqueDistricts.map((district) => (
                      <option key={district} value={district}>
                        {district}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Block
                  </label>
                  <select
                    value={filters.block}
                    onChange={(e) =>
                      handleFilterChange("block", e.target.value)
                    }
                    className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                  >
                    <option value="">All Blocks</option>
                    {uniqueBlocks.map((block) => (
                      <option key={block} value={block}>
                        {block}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Sector
                  </label>
                  <select
                    value={filters.sector}
                    onChange={(e) =>
                      handleFilterChange("sector", e.target.value)
                    }
                    className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500 focus:border-purple-500 bg-white"
                  >
                    <option value="">All Sectors</option>
                    {sectorOptions.map((sector) => (
                      <option key={sector} value={sector}>
                        {sector}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Sub Sector
                  </label>
                  <select
                    value={filters.subSector}
                    onChange={(e) =>
                      handleFilterChange("subSector", e.target.value)
                    }
                    className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500 focus:border-purple-500 bg-white"
                  >
                    <option value="">All Sub Sectors</option>
                    {subSectorOptions.map((subSector) => (
                      <option key={subSector} value={subSector}>
                        {subSector}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Financial Year
                  </label>
                  <select
                    value={filters.financialYear}
                    onChange={(e) =>
                      handleFilterChange("financialYear", e.target.value)
                    }
                    className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-orange-500 focus:border-orange-500 bg-white"
                  >
                    <option value="">All FY</option>
                    {financialYearOptions.map((year) => (
                      <option key={year} value={year}>
                        {year}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Scheme Status
                  </label>
                  <select
                    value={filters.schemeStatus}
                    onChange={(e) =>
                      handleFilterChange("schemeStatus", e.target.value)
                    }
                    className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-green-500 focus:border-green-500 bg-white"
                  >
                    <option value="">All Status</option>
                    {schemeStatusOptions.map((status) => (
                      <option key={status} value={status}>
                        {status}
                      </option>
                    ))}
                  </select>
                </div>

              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Results Count and Pagination Controls */}
        <div className="mb-4 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <p className="text-gray-600 text-sm">
              Showing{" "}
              <span className="font-semibold text-blue-600">{schemes.length}</span>{" "}
              of{" "}
              <span className="font-semibold text-blue-600">{pagination.totalCount}</span>{" "}
              legacy schemes
            </p>
            <p className="text-gray-500 text-xs mt-1">
              Page {pagination.pageNo} of {pagination.totalPages}
            </p>
          </div>

          <div className="flex items-center space-x-4">
            <FormControl size="small" sx={{ minWidth: 100 }}>
              <InputLabel>Show</InputLabel>
              <Select
                value={pagination.pageSize}
                label="Show"
                onChange={handlePageSizeChange}
              >
                <MenuItem value={5}>5</MenuItem>
                <MenuItem value={10}>10</MenuItem>
                <MenuItem value={25}>25</MenuItem>
                <MenuItem value={50}>50</MenuItem>
                <MenuItem value={100}>100</MenuItem>
              </Select>
            </FormControl>

            {pagination.totalPages > 0 && (
              <Box>
                <Pagination
                  count={pagination.totalPages}
                  page={pagination.pageNo}
                  onChange={handlePageChange}
                  color="primary"
                  size="medium"
                  showFirstButton
                  showLastButton
                  siblingCount={1}
                  boundaryCount={1}
                />
              </Box>
            )}
          </div>
        </div>

        {/* Schemes Table */}
        <div className="bg-white rounded-lg shadow border border-gray-200 overflow-hidden">
          {schemes.length === 0 ? (
            <div className="text-center py-12">
              <div className="text-4xl mb-3 text-gray-300">🔍</div>
              <h3 className="text-lg font-semibold text-gray-900 mb-1">No legacy schemes found</h3>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-4 py-3 text-left font-semibold text-gray-700">Proposal</th>
                    <th className="px-4 py-3 text-left font-semibold text-gray-700">Scheme</th>
                    <th className="px-4 py-3 text-left font-semibold text-gray-700">Location</th>
                    <th className="px-4 py-3 text-left font-semibold text-gray-700">Sector</th>
                    <th className="px-4 py-3 text-left font-semibold text-gray-700 cursor-pointer hover:bg-gray-100" onClick={() => handleSort("allocatedAmount")}>
                      <div className="flex items-center">
                        Amount
                        {sortConfig.key === "allocatedAmount" && (
                          <span className="ml-1 text-blue-600">
                            {sortConfig.direction === "asc" ? "↑" : "↓"}
                          </span>
                        )}
                      </div>
                    </th>
                    <th className="px-4 py-3 text-left font-semibold text-gray-700">Action</th>
                    {user?.userType === "admin" && (
                      <th className="px-4 py-3 text-left font-semibold text-gray-700 text-center">Delete</th>
                    )}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {schemes.map((scheme, index) => (
                    <motion.tr
                      key={scheme._id || index}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: index * 0.05 }}
                      className="hover:bg-blue-50 cursor-pointer transition-colors"
                      onClick={() => {
                        scheme?.formStatus === "completed"
                          ? navigate("../schemeDetails", {
                            state: { schemeId: scheme?.id },
                          })
                          : navigate((scheme?.schemeType === "existing" || scheme?.schemeType === "Legacy") ? "../existingSchemeEntry" : "../addNewScheme", {
                            state: {
                              draftSchemeId: scheme?.id,
                              draftSchemeData: scheme,
                            },
                          });
                      }}
                    >
                      <td className="px-4 py-3">{scheme.proposalName || "---"}</td>
                      <td className="px-4 py-3">
                        <div>
                          <div className="font-semibold text-gray-900 line-clamp-1">{scheme.schemeName}</div>
                          <div className="text-gray-500 text-xs mt-1">FY: {scheme.financialYear}</div>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="font-medium text-gray-900">{scheme.district}</div>
                        <div className="text-gray-500 text-xs">{scheme.block} • {scheme.gramPanchayat}</div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="font-medium text-gray-900">{scheme.sector}</div>
                        <div className="text-gray-500 text-xs">{scheme.subSector}</div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="font-bold text-blue-600">{formatAmount(scheme.allocatedAmount)}</div>
                      </td>
                      <td className="px-4 py-3" onMouseDown={(e) => e.stopPropagation()}>
                        <div className="flex items-center gap-3">
                          {scheme?.formStatus === "completed" && (
                            <button
                              className="font-bold text-blue-600 hover:text-blue-800"
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate("../schemeDetails", {
                                  state: { schemeId: scheme?.id },
                                });
                              }}
                            >
                              View
                            </button>
                          )}
                          {scheme?.formStatus === "draft" && (
                            <button
                              className="font-bold text-blue-600 hover:text-blue-800"
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate((scheme?.schemeType === "existing" || scheme?.schemeType === "Legacy") ? "../existingSchemeEntry" : "../addNewScheme", {
                                  state: {
                                    draftSchemeId: scheme?.id,
                                    draftSchemeData: scheme,
                                  },
                                });
                              }}
                            >
                              Edit
                            </button>
                          )}
                        </div>
                      </td>
                      {user?.userType === "admin" && (
                        <td className="px-4 py-3 text-center" onMouseDown={(e) => e.stopPropagation()}>
                          <button
                            className="font-bold text-red-600 hover:text-red-800"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDelete(scheme?.id);
                            }}
                          >
                            <FaTrash />
                          </button>
                        </td>
                      )}
                    </motion.tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Bottom Pagination */}
        {schemes.length > 0 && (
          <div className="mt-6 flex justify-center">
            <Box>
              <Pagination
                count={pagination.totalPages}
                page={pagination.pageNo}
                onChange={handlePageChange}
                color="primary"
                size="large"
                showFirstButton
                showLastButton
                siblingCount={1}
                boundaryCount={1}
              />
            </Box>
          </div>
        )}
      </div>
    </div>
  );
};

export default LegacySchemesList;
